/**
 * MeetingSummaryFullPage — Full-page view for a meeting summary (ahoy_meeting_notes).
 *
 * Document-style layout with meeting metadata, summary/action items/key topics,
 * and collapsible full transcript. Right column reserved for future recommended
 * actions sidebar.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { useUser } from '@/allauth_auth/hooks';
import { useOrg } from '@/contexts/OrgContext';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Button } from '@/lib/base/buttons/button';
import type { HeaderAction } from '@/lib/base/page-layout/page-layout';
import { PageLayout } from '@/lib/base/page-layout/page-layout';
import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { buildEntityBadgeData } from '@/lib/crm/components/badges/entityBadgeHelpers';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { useAssociations } from '@/lib/crm/hooks/useAssociations';
import { useMeetingNoteOwners } from '@/lib/crm/hooks/useMeetingNoteOwners';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { CRMDetailSkeleton } from '@/lib/crm/record/components/CRMDetailSkeleton';
import { TranscriptViewer } from '@/lib/crm/record/components/TranscriptViewer';
import { useCRMObject } from '@/lib/crm/record/hooks/useCRMObject';
import { CollapsibleSection } from '@/lib/crm/record/sections/collapsible-section';
import { enrichWithSpeakerMap } from '@/lib/crm/record/utils/meetingEnrichment';
import { getGuestDisplayName } from '@/lib/crm/record/utils/meetingHelpers';
import { getAvatarInitials, getGravatarUrl } from '@/lib/crm/utils/avatar';
import {
  MEETING_NOTES_PROVIDER_PATHS,
  MEETING_NOTES_SOURCE_LABELS,
  MeetingNoteAttribution,
} from '@/lib/crm/utils/meetingNotes';
import { apiGet } from '@/lib/http/client';
import {
  createEntityMarkdownComponents,
  entitySanitizeSchema,
  preprocessEntityTags,
} from '@/lib/markdown/entityRendering';
import type { TimeFormat } from '@/lib/time';
import { timeParts, useOrgTimeFormat } from '@/lib/time';
import type { CalendarEventGuest } from '@/pages/calendar/utils/eventMapper';
import { mapInvitationsToGuests } from '@/pages/calendar/utils/eventMapper';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

// ============================================================================
// Static config
// ============================================================================

const REHYPE_PLUGINS = [
  rehypeRaw,
  [rehypeSanitize, entitySanitizeSchema],
] as unknown[];

const SOURCE_ICONS: Record<string, string> = {
  google_meet: '/assets/figma/google-meet-icon.svg',
  granola: '/assets/figma/granola-icon.svg',
  zoom: '/assets/figma/zoom-icon.svg',
  fathom: '/assets/figma/fathom-icon.svg',
};

// ============================================================================
// Time formatting
// ============================================================================

function formatMeetingTime(
  startTime: string | null,
  endTime: string | null,
  format: TimeFormat
): string | null {
  if (!startTime) {
    return null;
  }

  const start = new Date(startTime);
  if (isNaN(start.getTime())) {
    return null;
  }

  const datePart = start.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const startTimePart = start.toLocaleTimeString(undefined, timeParts(format));

  if (!endTime) {
    return `${datePart} • ${startTimePart}`;
  }

  const end = new Date(endTime);
  if (isNaN(end.getTime())) {
    return `${datePart} • ${startTimePart}`;
  }

  const endTimePart = end.toLocaleTimeString(undefined, {
    ...timeParts(format),
    timeZoneName: 'short',
  });

  const durationMs = end.getTime() - start.getTime();
  const durationMins = Math.round(durationMs / 60_000);
  const durationPart = durationMins > 0 ? `${durationMins} mins` : '';

  const parts = [datePart, `${startTimePart} - ${endTimePart}`];
  if (durationPart) {
    parts.push(durationPart);
  }

  return parts.join(' • ');
}

// ============================================================================
// Transcript types
// ============================================================================

interface TranscriptResponse {
  transcript_content: string;
  entity_id: string;
  content_type?: string;
}

function getTranscriptUrl(
  orgId: number,
  entityId: string,
  source: string | undefined
): string {
  const pathSegment =
    (source && MEETING_NOTES_PROVIDER_PATHS[source]) || 'meetings';
  return `/api/orgs/${orgId}/${pathSegment}/notes/${entityId}/transcript/`;
}

// ============================================================================
// Attendee chip (matches summary-section.tsx)
// ============================================================================

function AttendeeChip({ guest }: { guest: CalendarEventGuest }) {
  const displayName = getGuestDisplayName(guest);
  const gravatarUrl = getGravatarUrl(guest.email, 48);
  const tooltipText = `${guest.name || guest.email.split('@')[0]} · ${guest.email}`;

  return (
    <span
      className='size-max flex items-center gap-1 whitespace-nowrap rounded-full py-0.5 pl-0.75 pr-2 text-xs font-medium ring-1 ring-inset bg-utility-gray-50 text-utility-gray-700 ring-utility-gray-200 [&_[data-avatar]>span]:text-[9px]'
      title={tooltipText}
    >
      <Avatar
        size='xxs'
        src={gravatarUrl}
        alt={displayName}
        initials={getAvatarInitials(guest.name || guest.email)}
      />
      <span className='whitespace-nowrap'>{displayName}</span>
    </span>
  );
}

// ============================================================================
// Attendee badge — pre-resolved contact rendered via the EntityBadge primitive.
// Skips EntityBadgeById's hydration wrapper because `contactObject` is already
// resolved upstream (avoids unnecessary loading/failed transitions and store
// lookups).
// ============================================================================

function AttendeeContactBadge({ object }: { object: CrmObject }) {
  const openSlideout = useOpenSlideoutOptional();
  const entityId = object.entity_id;

  const handleClick = useCallback(() => {
    openSlideout?.(entityId);
  }, [openSlideout, entityId]);

  const state = useMemo(
    () => ({ kind: 'data' as const, data: buildEntityBadgeData(object) }),
    [object]
  );

  return (
    <EntityBadge
      state={state}
      variant='xs'
      onClick={openSlideout ? handleClick : undefined}
    />
  );
}

// ============================================================================
// Main content component (receives loaded object)
// ============================================================================

export function MeetingSummaryContent({ object }: { object: CrmObject }) {
  const orgId = object.org_id;
  const { timeFormat } = useOrgTimeFormat();

  // Extract meeting notes properties
  const title =
    getPropertyValue<string>(object.properties?.ahoy_title) ||
    'Meeting Summary';
  const source = getPropertyValue<string>(object.properties?.ahoy_notes_source);
  const summary = getPropertyValue<string>(object.properties?.ahoy_summary);
  const actionItems = getPropertyValue<string>(
    object.properties?.ahoy_action_items
  );
  const keyTopics = getPropertyValue<string>(
    object.properties?.ahoy_key_topics
  );

  const sourceLabel = source
    ? (MEETING_NOTES_SOURCE_LABELS[source] ?? source)
    : null;
  const sourceIcon = source ? SOURCE_ICONS[source] : null;

  // Owner resolution
  const ownerNotes = useMemo(() => [object], [object]);
  const ownerByNote = useMeetingNoteOwners(ownerNotes);
  const ownerEntityId = ownerByNote.get(object.entity_id);

  // Speaker map
  const speakerMap = useMemo(() => {
    const raw = getPropertyValue<string>(object.properties?.ahoy_speaker_map);
    if (!raw) {
      return {};
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        return {};
      }
      // Validate each entry has the expected shape
      const result: Record<string, { entity_type: string; entity_id: string }> =
        {};
      for (const [key, value] of Object.entries(
        parsed as Record<string, unknown>
      )) {
        if (
          typeof value === 'object' &&
          value !== null &&
          typeof (value as Record<string, unknown>).entity_type === 'string' &&
          typeof (value as Record<string, unknown>).entity_id === 'string'
        ) {
          result[key] = value as { entity_type: string; entity_id: string };
        }
      }
      return result;
    } catch {
      return {};
    }
  }, [object.properties?.ahoy_speaker_map]);

  const markdownComponents = useMemo(
    () => createEntityMarkdownComponents(orgId),
    [orgId]
  );

  // Resolve meeting association
  const noteAssociations = useMemo(
    () => object.associations ?? [],
    [object.associations]
  );
  const { associations: resolvedNoteAssociations } = useAssociations(
    orgId,
    noteAssociations
  );

  const meetingObject = useMemo(() => {
    const meetingAssoc = resolvedNoteAssociations.find(
      a =>
        a.association_type_name === 'ahoy_meeting_notes_to_meeting' && a.object
    );
    return meetingAssoc?.object ?? null;
  }, [resolvedNoteAssociations]);

  // Resolve meeting associations for attendee matching
  const meetingAssociations = useMemo(
    () => meetingObject?.associations ?? [],
    [meetingObject?.associations]
  );
  const { associations: resolvedMeetingAssociations } = useAssociations(
    orgId,
    meetingAssociations,
    { includeAssociations: false }
  );

  const { members } = useOrgMembers(orgId);

  // Meeting time
  const startTime = meetingObject
    ? (getPropertyValue<string>(meetingObject.properties?.ahoy_start_time) ??
      null)
    : null;
  const endTime = meetingObject
    ? (getPropertyValue<string>(meetingObject.properties?.ahoy_end_time) ??
      null)
    : null;
  const timeStr = useMemo(
    () => formatMeetingTime(startTime, endTime, timeFormat),
    [startTime, endTime, timeFormat]
  );

  // Parse invitations to guests
  const guests = useMemo(() => {
    if (!meetingObject) {
      return [];
    }
    const invitations = getPropertyValue<
      Array<{
        email?: string;
        invitee_name?: string;
        response_status?: string;
        is_organizer?: string;
        is_optional?: string;
      }>
    >(meetingObject.properties?.ahoy_invitations);
    return mapInvitationsToGuests(invitations);
  }, [meetingObject]);

  // Build email→CrmObject map for attendee→contact matching
  const contactsByEmail = useMemo(() => {
    const map = new Map<string, CrmObject>();
    for (const assoc of resolvedMeetingAssociations) {
      if (
        (assoc.association_type_name === 'ahoy_meeting_to_contact' ||
          assoc.association_type_name === 'ahoy_meeting_to_user') &&
        assoc.object
      ) {
        const rawEmails = getPropertyValue<string | string[]>(
          assoc.object.properties?.ahoy_email
        );
        let emails: string[] = [];
        if (Array.isArray(rawEmails)) {
          emails = rawEmails;
        } else if (typeof rawEmails === 'string') {
          emails = [rawEmails];
        }
        for (const email of emails) {
          if (typeof email === 'string' && email) {
            map.set(email.toLowerCase(), assoc.object);
          }
        }
      }
    }
    // Fallback: map user associations via org membership auth email
    for (const assoc of resolvedMeetingAssociations) {
      if (
        assoc.association_type_name === 'ahoy_meeting_to_user' &&
        assoc.object
      ) {
        const assocEntityId = assoc.object?.entity_id;
        const member = members.find(m => m.entityId === assocEntityId);
        if (member?.email && !map.has(member.email.toLowerCase())) {
          map.set(member.email.toLowerCase(), assoc.object);
        }
      }
    }
    return map;
  }, [resolvedMeetingAssociations, members]);

  // Transcript (lazy-loaded on expand)
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const {
    data: transcriptData,
    isLoading: isLoadingTranscript,
    error: transcriptError,
    refetch: refetchTranscript,
  } = useQuery({
    queryKey: ['meetingTranscript', orgId, object.entity_id, source],
    queryFn: async () => {
      return apiGet<TranscriptResponse>(
        getTranscriptUrl(orgId, object.entity_id, source)
      );
    },
    retry: 1,
    staleTime: Infinity,
    enabled: isTranscriptOpen,
  });

  const handleRetryTranscript = useCallback(() => {
    refetchTranscript();
  }, [refetchTranscript]);

  const pageTitle = `Meeting summary: ${title}`;

  const handleShare = useCallback(() => {
    void navigator.clipboard.writeText(window.location.href);
  }, []);

  const headerActions = useMemo<HeaderAction[]>(
    () => [
      {
        id: 'share',
        label: 'Share',
        variant: 'secondary',
        onClick: handleShare,
      },
    ],
    [handleShare]
  );

  return (
    <PageLayout title={pageTitle} actions={headerActions}>
      <div className='flex-1 overflow-y-auto'>
        <div className='max-w-3xl px-6 py-6'>
          {/* Time */}
          {timeStr && (
            <p className='text-sm text-fg-secondary mb-3'>{timeStr}</p>
          )}

          {/* Source + attendees row */}
          <div className='flex items-center gap-4 mb-6'>
            {(sourceLabel || ownerEntityId) && (
              <div className='flex items-center gap-1.5'>
                <MeetingNoteAttribution
                  sourceLabel={sourceLabel ?? undefined}
                  sourceIcon={sourceIcon ?? undefined}
                  ownerEntityId={ownerEntityId}
                  orgId={orgId}
                  interactive={true}
                  variant='fullpage'
                  alwaysShowSeparator={false}
                />
              </div>
            )}

            {guests.length > 0 && (
              <div className='flex items-center gap-1.5 flex-wrap'>
                {guests
                  .filter(g => typeof g.email === 'string')
                  .slice(0, 6)
                  .map(guest => {
                    const contactObject = contactsByEmail.get(
                      guest.email.toLowerCase()
                    );
                    if (contactObject) {
                      return (
                        <AttendeeContactBadge
                          key={guest.email}
                          object={contactObject}
                        />
                      );
                    }
                    return <AttendeeChip key={guest.email} guest={guest} />;
                  })}
                {guests.length > 6 && (
                  <span className='text-xs text-fg-tertiary ml-1'>
                    +{guests.length - 6} more
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Meeting Summary */}
          {summary && (
            <div className='mb-8'>
              <h2 className='text-sm font-semibold text-[var(--color-fg-brand-primary)] mb-2'>
                Meeting Summary
              </h2>
              <div className='text-sm text-fg-primary leading-relaxed'>
                <MarkdownContent
                  content={preprocessEntityTags(
                    enrichWithSpeakerMap(summary, speakerMap)
                  )}
                  rehypePlugins={REHYPE_PLUGINS}
                  components={markdownComponents}
                  className='text-sm'
                />
              </div>
            </div>
          )}

          {/* Action Items */}
          {actionItems && (
            <div className='mb-8'>
              <h2 className='text-sm font-semibold text-fg-primary mb-2'>
                Action Items
              </h2>
              <div className='text-sm text-fg-primary leading-relaxed'>
                <MarkdownContent
                  content={preprocessEntityTags(
                    enrichWithSpeakerMap(actionItems, speakerMap)
                  )}
                  rehypePlugins={REHYPE_PLUGINS}
                  components={markdownComponents}
                  className='text-sm'
                />
              </div>
            </div>
          )}

          {/* Key Topics */}
          {keyTopics && (
            <div className='mb-8'>
              <h2 className='text-sm font-semibold text-fg-primary mb-2'>
                Key Topics
              </h2>
              <div className='text-sm text-fg-primary leading-relaxed'>
                <MarkdownContent
                  content={preprocessEntityTags(
                    enrichWithSpeakerMap(keyTopics, speakerMap)
                  )}
                  rehypePlugins={REHYPE_PLUGINS}
                  components={markdownComponents}
                  className='text-sm'
                />
              </div>
            </div>
          )}

          {/* Full transcript (collapsible, lazy-loaded) */}
          <CollapsibleSection
            title='Full transcript'
            defaultExpanded={false}
            onExpandedChange={setIsTranscriptOpen}
          >
            {isLoadingTranscript && (
              <div className='text-sm text-fg-tertiary py-4'>
                Loading transcript…
              </div>
            )}
            {transcriptError && (
              <div className='space-y-2 py-4'>
                <p className='text-sm text-[var(--color-text-error-primary)]'>
                  We couldn't load the transcript.
                </p>
                <Button
                  color='secondary'
                  size='sm'
                  onClick={handleRetryTranscript}
                >
                  Retry
                </Button>
              </div>
            )}
            {transcriptData && (
              <TranscriptViewer
                content={transcriptData.transcript_content}
                contentType={transcriptData.content_type || 'text/plain'}
                orgId={orgId}
              />
            )}
          </CollapsibleSection>
        </div>
      </div>
    </PageLayout>
  );
}

// ============================================================================
// Page wrapper (handles data loading and error states)
// ============================================================================

export default function MeetingSummaryFullPage() {
  const { recordId } = useParams<{ recordId: string }>();
  const { currentOrg, isLoading: orgLoading } = useOrg();
  const user = useUser();

  const { object, loading, error } = useCRMObject(
    currentOrg?.id,
    'ahoy_meeting_notes',
    recordId,
    user?.id ?? 0
  );

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  if (loading || orgLoading) {
    return <CRMDetailSkeleton />;
  }

  if (error) {
    return (
      <div className='p-6' role='alert'>
        <div className='p-4 bg-utility-error-50 border border-utility-error-200 rounded-xl'>
          <p className='text-utility-error-900 font-medium'>
            Unable to load meeting summary
          </p>
          <p className='text-sm text-utility-error-700 mt-1'>{error}</p>
          <button
            onClick={handleReload}
            className='mt-3 text-sm text-utility-error-800 underline hover:no-underline cursor-pointer bg-transparent border-none p-0'
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!object) {
    return (
      <div className='p-6'>
        <div className='p-4 bg-utility-warning-50 border border-utility-warning-200 rounded-xl'>
          <p className='text-utility-warning-900 font-medium'>
            Meeting summary not found
          </p>
        </div>
      </div>
    );
  }

  return <MeetingSummaryContent object={object} />;
}
